import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { KeyValueDTO, User } from '@ms/core';
import { PrincipalService } from '@ms/core/auth/principal.service';
import { DialogService } from '@ms/core/components/dialog/service/dialog.service';
import { LoginService } from '@ms/core/components/login/login.service';
import { EventManager } from '@ms/core/service/event-manager.service';
import { TranslateService } from '@ngx-translate/core';
import { AttachmentInfo } from 'app/main/shared/attachments/user-attachment.model';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';
import { IncidentSettings } from './../incident/incident-list/incident.model';

import { inject } from '@angular/core';
import { logRequest } from 'app/main/shared/http-utils';
import { HTTP_CLIENT_XHR } from 'app/system/http-client-provider';
import { ThemeLoaderService } from 'app/system/theme-loader.service';
import { SalaryConstants, SalaryConstantsSearch } from '../salary-constants/salary-constants.model';
import { MailServer } from '../schools/model/mail-server.model';
import { SchoolConstant } from '../schools/model/school-constant.model';
import { ISchool, MeetingServer, RoleMessageSettings, School, TwilioCredentials } from '../schools/model/school.model';
import { PaymentSettings } from '../schools/school-list/school-form/payment-setting/payment-setting.model';
import { SchoolSearch } from '../schools/school-list/schools-search.model';
import { Language } from '../shared/model/translateble-string.model';
import { convertRequest } from '../shared/utils';
import { SchoolSettings } from './../schools/model/school.model';
import { FileUploadService } from './file-upload.service';

@Injectable({
    providedIn: 'root',
})
export class SchoolService implements FileUploadService {
    baseUrl = '/school-service/api/schools';

    xhrHttp = inject(HTTP_CLIENT_XHR);
    constructor(
        private http: HttpClient,
        private principalService: PrincipalService,
        private cookieService: CookieService,
        private translateService: TranslateService,
        private eventManager: EventManager,
        private dialogService: DialogService,
        private loginService: LoginService,
        private themeLoader: ThemeLoaderService
    ) {}

    getSchoolInfo(account: User) {
        // Get user schools
        const schoolIds = this.principalService.getProperty('SCHOOL_ID')
            ? this.principalService.getProperty('SCHOOL_ID').split(',')
            : null;
        console.log('SCHOOLS', schoolIds, account);
        if (schoolIds?.length > 0) {
            this.getSchoolsByIds(schoolIds).subscribe(
                result => {
                    account = this.principalService.authenticatedUser;
                    if (result?.body?.length > 0) {
                        //Only active schools
                        account.schools = result.body.slice().filter(s => s.active == true);
                    }
                    if (account.schools?.length > 0) {
                        const activeSchoolCookieExist = this.cookieService.check(`${account.id}_active_school`);
                        // If cookie exists
                        if (activeSchoolCookieExist) {
                            const activeSchoolId = this.cookieService.get(`${account.id}_active_school`);
                            const activeSchool = account.schools.find(r => r.id == activeSchoolId);
                            if (!activeSchool) {
                                account.activeSchool = account.schools[0];
                                account.academicYear = account.schools[0].activeAcademicYear;
                            } else {
                                account.activeSchool = activeSchool;
                                account.academicYear = activeSchool.activeAcademicYear;
                            }
                        } else {
                            account.activeSchool = account.schools[0];
                            account.academicYear = account.schools[0].activeAcademicYear;
                        }

                        console.log('USER_DATA', account);

                        console.log('ACCOUNT', account);
                        this.cookieService.set(`${account.id}_active_school`, account.activeSchool.id);
                        if (!localStorage.getItem('theme') && account.activeSchool.schoolSettings?.theme) {
                            this.themeLoader.init(account.activeSchool.schoolSettings?.theme);
                            localStorage.setItem('theme', account.activeSchool.schoolSettings?.theme);
                        }
                        this.completeAccountInitialization(account);
                    } else {
                        this.emptySchoolAssign(account);
                        this.cookieService.delete(`${account.id}_active_school`);
                        this.completeAccountInitialization(account);
                    }
                    this.schoolChanged(account);
                },
                () => {
                    setTimeout(() => {
                        this.loginService.logout();
                        this.eventManager.broadcast({
                            name: 'show-login',
                            content: {},
                        });
                    });
                    this.dialogService.error('Service is temporarily unavailable. Please try again later');
                }
            );
        } else {
            this.emptySchoolAssign(account);
            this.schoolChanged(account);
            this.principalService.updateUserState(account, true);
        }
    }

    hasValue(key: string, value: string): boolean {
        const user = this.principalService.authenticatedUser;
        const license = user.activeSchool?.constants?.find(c => c.key == key);
        return license?.value == value;
    }

    completeAccountInitialization(account: User) {
        if (!account.academicYear) {
            account.academicYear = { id: '111111111111111111111111', name: 'No-Period' };
            account.activeSchool.activeAcademicYear = { id: '111111111111111111111111', name: 'No-Period' };
            this.dialogService.warning(this.translateService.instant('GENERAL.MESSAGE.INFO.ACADEMIC_PERIOD_IS_EMPTY'));
        }
        this.principalService.updateUserState(account, false);
        sessionStorage.setItem('currentUser', JSON.stringify(account));
        this.eventManager.broadcast({
            name: 'login_completed',
            account,
        });
    }

    schoolChanged(user: User) {
        this.principalService.updateUserState(user, true);
        sessionStorage.setItem('currentUser', JSON.stringify(user));
    }

    emptySchoolAssign(user: User) {
        user.schools = [];

        user.activeSchool = {
            id: '5ba3fc8b6ed2c5f2b815faf1',
            name: 'NOT-ASSIGNED',
            shortName: 'NOT-ASSIGNED',
            language: Language.en,
            active: true,
            schoolSettings: {
                academicYearPositionBottom: '20px',
                academicYearPositionLeft: '80px',
                logoWidth: '200px',
                toolbarHeight: 74,
                theme: 'default-theme',
            },
        };
        this.dialogService.warning(this.translateService.instant('GENERAL.MESSAGE.WARNING.SCHOOL_NOT_ASSIGNED'), {
            duration: 10000,
            position: 'top-center',
        });
    }

    getActiveSchool(user?: User): School {
        if (user) {
            return user.activeSchool;
        } else {
            const isAuthenticated = this.principalService.isAuthenticated;
            if (isAuthenticated) {
                return Object.assign({}, this.principalService.authenticatedUser.activeSchool);
            } else {
                return null;
            }
        }
    }

    getSchools(): Observable<ISchool[]> {
        return this.http.get<ISchool[]>(`${this.baseUrl}/${this.principalService.authenticatedUser.tenantId}/tenant`);
    }

    getSchoolsByTenantId(tenantId: string): Observable<ISchool[]> {
        return this.http.get<ISchool[]>(`${this.baseUrl}/${tenantId}/tenant`);
    }

    getSchoolById(id: string): Observable<HttpResponse<ISchool>> {
        return this.http.get<ISchool>(`${this.baseUrl}/${id}`, {
            observe: 'response',
        });
    }

    getCardBackgroundImages(): Observable<AttachmentInfo[]> {
        return this.http.get<AttachmentInfo[]>(`${this.baseUrl}/${this.getActiveSchool().id}/card-background-images`);
    }

    createSchool(school: ISchool): Observable<HttpResponse<ISchool>> {
        return this.http.post<ISchool>(this.baseUrl, school, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    updateSchool(school: School, formData: FormData) {
        if (!formData) {
            formData = new FormData();
        }
        if (school) {
            school = convertRequest(school);
            formData.set(
                'dto',
                new Blob([JSON.stringify(school)], {
                    type: 'application/json',
                })
            );
        }
        return this.xhrHttp.put<School>(this.baseUrl, formData, {
            reportProgress: true,
            observe: 'events',
        });
    }

    deleteSchool(id: string): Observable<HttpResponse<any>> {
        return this.http.delete(`${this.baseUrl}/${id}`, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    requestDeleteSchool(id: string): Observable<HttpResponse<any>> {
        return this.http.post(
            `${this.baseUrl}/request-deletion`,
            { schoolId: id },
            {
                observe: 'response',
                headers: logRequest(),
            }
        );
    }

    map(response: ISchool): ISchool {
        const result: ISchool = Object.assign({}, response);
        result.contactInfo = Object.assign({}, result.contactInfo);
        result.address = Object.assign({}, result.address);
        result.address.country = Object.assign({ id: '' }, result.address.country);
        result.mailServer = Object.assign(result.mailServer ? result.mailServer : new MailServer());
        result.paymentSettings = Object.assign(result.paymentSettings ? result.paymentSettings : new PaymentSettings());
        result.schoolSettings = Object.assign(result.schoolSettings ? result.schoolSettings : new SchoolSettings());
        result.roleMessageSettings = Object.assign(
            result.roleMessageSettings ? result.roleMessageSettings : new RoleMessageSettings()
        );
        return result;
    }

    searchSchools(schoolSearchDTO: SchoolSearch): Observable<HttpResponse<ISchool[]>> {
        return this.http.post<ISchool[]>(`${this.baseUrl}/search`, schoolSearchDTO, { observe: 'response' });
    }

    searchSalaryConstants(
        schoolId: string,
        salaryConstantsSearchDTO: SalaryConstantsSearch
    ): Observable<HttpResponse<SalaryConstants[]>> {
        return this.http.post<SalaryConstants[]>(
            `${this.baseUrl}/${schoolId}/salary-constants-search`,
            salaryConstantsSearchDTO,
            { observe: 'response' }
        );
    }

    addSalaryConstant(schoolId: string, salaryConstant: SalaryConstants): Observable<HttpResponse<any>> {
        return this.http.post(`${this.baseUrl}/${schoolId}/salary-constants`, salaryConstant, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    updateSalaryConstant(schoolId: string, salaryConstant: SalaryConstants): Observable<HttpResponse<any>> {
        return this.http.put(`${this.baseUrl}/${schoolId}/salary-constants`, salaryConstant, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    deleteSalaryConstant(schoolId: string, constantId: string): Observable<HttpResponse<SalaryConstants>> {
        return this.http.delete(`${this.baseUrl}/${schoolId}/salary-constants` + `/${constantId}`, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    getAllActiveForSelection(): Observable<KeyValueDTO[]> {
        return this.http.get<KeyValueDTO[]>(
            `${this.baseUrl}/tenant/${this.principalService.authenticatedUser.tenantId}/select`
        );
    }

    checkMailServerConnection(mailServer: MailServer, schoolId: string): Observable<HttpResponse<any>> {
        return this.http.post(`${this.baseUrl}/check-mail-server/${schoolId}`, mailServer, {
            observe: 'response',
            headers: logRequest(),
        });
    }
    checkTwilioConnection(twilioCredentials: TwilioCredentials, schoolId: string): Observable<HttpResponse<any>> {
        return this.http.post(`${this.baseUrl}/check-twilio-connection/${schoolId}`, twilioCredentials, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    getSchoolsByIds(ids: string[]): Observable<HttpResponse<ISchool[]>> {
        const user = this.principalService.authenticatedUser;
        return this.http.post<ISchool[]>(
            `${this.baseUrl}/tenant-schools`,
            { tenantId: user.tenantId, schoolIds: ids },
            {
                observe: 'response',
            }
        );
    }

    getAllForSelectionList(tenantId: string): Observable<KeyValueDTO[]> {
        return this.http.get<KeyValueDTO[]>(`${this.baseUrl}/tenant/${tenantId}/select`);
    }

    checkBBBServer(server: MeetingServer, schoolId: string): Observable<HttpResponse<any>> {
        return this.http.post(`${this.baseUrl}/bbb-server-check/${schoolId}`, server, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    downloadFile(schoolId: string, attachmentUrl: string) {
        return this.http.get(`${this.baseUrl}/${schoolId}/attachment/${attachmentUrl}/download`, {
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    deleteFile(schoolId: string, attachmentUrl: string) {
        if (attachmentUrl.match(/\.(jpg|jpeg|png|gif)$/i)) {
            const dotIndex = attachmentUrl.lastIndexOf('.');
            attachmentUrl = `${attachmentUrl.substring(0, dotIndex)}+${attachmentUrl.substring(dotIndex + 1)}`;

            return this.http.delete(`${this.baseUrl}/${schoolId}/attachment/${attachmentUrl}/image`, {
                headers: logRequest(),
            });
        }
        return this.http.delete(
            `${this.baseUrl}/${schoolId}/attachment/${attachmentUrl}?time=${new Date().getTime()}`,
            {
                headers: logRequest(),
            }
        );
    }

    downloadByAttachmentURL(attachmentUrl: string) {
        throw Error(`This method not implemented yet. ${attachmentUrl}`);
    }

    checkAccountingSystemConnection(schoolConstants: SchoolConstant[]) {
        return this.http.post(`${this.baseUrl}/check-accounting-system-connection`, schoolConstants, {
            observe: 'response',
        });
    }

    setActive(id: string, isActive: boolean) {
        return this.http.patch(`${this.baseUrl}/${id}/active/${isActive}`, { headers: logRequest() });
    }

    getIncidentSettings(): Observable<IncidentSettings> {
        return this.http.get<IncidentSettings>(`${this.baseUrl}/${this.getActiveSchool().id}/incident-settings`);
    }

    downloadClassTeacherWorkPlan(): Observable<HttpResponse<any>> {
        return this.http.get(`${this.baseUrl}/download/class-teacher-work-plan`, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }
}
